import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import moment from 'moment';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import MyPageLayout from '../../components/Layouts/MyPage/MyPageLayout.index';
import './style/ReservationHistory.scss';
import './style/_button.scss';

import ReservationCancelModal, {
  DataType as ModalCancelReserveDataType,
} from './Modal/ReservationCancel/ReservationCancel';
import ReservationCancelCompleteModal from './Modal/ReservationCancel/Complete/ReservationCancelComplete';

import CancelWaitRemoveModal, {
  DataType as ModalRemoveWaitCancelDataType,
} from './Modal/CancelWaitRemove/CancelWaitRemove';
import CancelWaitRemoveCompleteModal from './Modal/CancelWaitRemove/Complete/CancelWaitRemoveComplete';

import { RootState } from '../../store/reducers';
import {
  GET_RESERVE_HISTORY_INITIAL,
  GET_RESERVE_HISTORY_REQUEST,
  PUT_CANCEL_RESERVE_INITIAL,
  PUT_CANCEL_RESERVE_REQUEST,
  PUT_REMOVE_WAIT_CANCEL_INITIAL,
  PUT_REMOVE_WAIT_CANCEL_REQUEST,
} from '../../store/actions';
import {
  ReserveDataType,
  GetReserveHistoryStateType,
} from '../../store/reducers/reserveHistory/getReserveHistoryReducer';
import { PutCancelReserveStateType } from '../../store/reducers/reserveHistory/putCancelReserveReducer';
import { PutRemoveWaitCancelStateType } from '../../store/reducers/reserveHistory/putRemoveWaitCancelReducer';

import useAuthorizationNormal from '../../hooks/Auth/useAuthorizationNormal';
import CustomLoading from '../../components/Common/Loading/CustomLoading.index';
import googleCalendarIcon from './style/img/google-calendar.svg';

dayjs.extend(utc);
dayjs.extend(timezone);

const ReservationHistory: FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { isGetReserveHistoryLoaded, getReserveHistory } = useSelector<RootState, GetReserveHistoryStateType>(
    (state) => state.reserveHistory.getReserveHistory,
  );

  const {
    isPutCancelReserveLoading,
    isPutCancelReserveLoaded,
    errMessage: cancelReserveErrMessage,
  } = useSelector<RootState, PutCancelReserveStateType>((state) => state.reserveHistory.putCancelReserve);

  const { isPutRemoveWaitCancelLoaded } = useSelector<RootState, PutRemoveWaitCancelStateType>(
    (state) => state.reserveHistory.putRemoveWaitCancel,
  );

  const [yearMonthList, setYearMonthList] = useState<string[]>([]);
  const [curYearMonthIndex, setCurYearMonthIndex] = useState<number>(-1);

  const [modalCancelReserveShow, setModalCancelReserveShow] = useState<boolean>(false);
  const [modalCancelReserveData, setModalCancelReserveData] = useState<ModalCancelReserveDataType>({
    data_id: 0,
    schedule_id: 0,
    contents_id: 0,
    contents_annex_flag: 0,
    contents_web_show_name: '',
    club_equipment_annex_flag: 0,
    club_web_show_name: '',
    reserve_time_label: '',
    alternate_label: '',
    installed_seats_name: '',
    follow_people_upper: '0',
    companion: 0,
  });
  const [isCancelReserveSuccess, setIsCancelReserveSuccess] = useState<boolean>(false);

  const [modalRemoveWaitCancelShow, setModalRemoveWaitCancelShow] = useState<boolean>(false);
  const [modalRemoveWaitCancelData, setModalRemoveWaitCancelData] = useState<ModalRemoveWaitCancelDataType>({
    data_id: 0,
    contents_annex_flag: 0,
    contents_web_show_name: '',
    club_equipment_annex_flag: 0,
    club_web_show_name: '',
    reserve_time_label: '',
  });
  const [isRemoveWaitCancelSuccess, setIsRemoveWaitCancelSuccess] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      dispatch({
        type: GET_RESERVE_HISTORY_INITIAL,
      });
      dispatch({
        type: PUT_CANCEL_RESERVE_INITIAL,
      });
      dispatch({
        type: PUT_REMOVE_WAIT_CANCEL_INITIAL,
      });
    };
  }, []);

  // ログイン認証を設定する
  useAuthorizationNormal();

  useEffect(() => {
    // handle year-month-list
    let tmpYearMonthList: string[] = [];
    const now = new Date();
    for (let i = -10; i <= 2; i += 1) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      tmpYearMonthList.push(year + month);
    }
    tmpYearMonthList = tmpYearMonthList.sort().reverse();

    setYearMonthList(tmpYearMonthList);
    setCurYearMonthIndex(2);
  }, [history, dispatch]);

  const fetchReserveHistoryData = () => {
    if (yearMonthList && curYearMonthIndex >= 0 && curYearMonthIndex <= yearMonthList.length - 1) {
      dispatch({
        type: GET_RESERVE_HISTORY_REQUEST,
        memberId: sessionStorage.getItem('memberID') ?? '',
        requestYearMonth: yearMonthList[curYearMonthIndex],
        history,
      });
    }
  };

  useEffect(() => {
    fetchReserveHistoryData();
  }, [yearMonthList, curYearMonthIndex]);

  const onClickNextYearMonth = () => {
    if (curYearMonthIndex > 0) {
      setCurYearMonthIndex(curYearMonthIndex - 1);
    }
  };

  const onClickPrevYearMonth = () => {
    if (curYearMonthIndex < yearMonthList.length - 1) {
      setCurYearMonthIndex(curYearMonthIndex + 1);
    }
  };

  const onChangeYearMonth = (e: React.ChangeEvent<HTMLSelectElement>) => {
    console.log('123', e);
    setCurYearMonthIndex(yearMonthList.indexOf(e.target.value));
  };

  const onClickCancelReserve = (item: ReserveDataType) => () => {
    if (item.schedule_id && item.contents_id) {
      setModalCancelReserveShow(true);
      setModalCancelReserveData({
        data_id: item.data_id,
        schedule_id: item.schedule_id,
        contents_id: item.contents_id,
        contents_annex_flag: item.contents_annex_flag,
        contents_web_show_name: item.contents_web_show_name,
        club_equipment_annex_flag: item.club_equipment_annex_flag,
        club_web_show_name: item.club_web_show_name,
        reserve_time_label: item.reserve_time_label,
        alternate_label: item.alternate_label,
        installed_seats_name: item.installed_seats_name,
        follow_people_upper: item.follow_people_upper,
        companion: item.companion,
      });
    }
  };

  const buildGoogleCalendarUrl = (params: { text: string; dates: string; location: string; details: string }) => {
    return (
      'https://calendar.google.com/calendar/render?action=TEMPLATE' +
      `&text=${encodeURIComponent(params.text)}` +
      `&dates=${encodeURIComponent(params.dates)}` +
      `&ctz=${encodeURIComponent('Asia/Tokyo')}` +
      `&location=${encodeURIComponent(params.location)}` +
      `&details=${encodeURIComponent(params.details)}`
    );
  };

  const onClickGoogleCalendarAdd = (item: ReserveDataType) => () => {
    if (item.schedule_id && item.contents_id) {
      const myPageReservationHistoryUrl = `${window.location.origin}/history`;
      const scheduleDate = dayjs(item.schedule_date).tz('Asia/Tokyo').format('YYYYMMDD');
      const endDate = item.end_time === '0000' ? dayjs(scheduleDate).add(1, 'day').format('YYYYMMDD') : scheduleDate;
      const dates = `${scheduleDate}T${item.start_time}00/${endDate}T${item.end_time}00`;
      const location = `${item.club_web_show_name}, ${item.address}`;
      const details = `${item.contents_web_show_name}\nスペース\u3000${item.alternate_label}\n\n<a href="${myPageReservationHistoryUrl}">予約の詳細</a>`;
      window.open(
        buildGoogleCalendarUrl({
          text: item.contents_web_show_name ?? '',
          dates,
          location,
          details,
        }),
        '_blank',
        'noopener,noreferrer',
      );
    }
  };

  useEffect(() => {
    if (!modalCancelReserveShow) {
      setModalCancelReserveData({
        data_id: 0,
        schedule_id: 0,
        contents_id: 0,
        contents_annex_flag: 0,
        contents_web_show_name: '',
        club_equipment_annex_flag: 0,
        club_web_show_name: '',
        reserve_time_label: '',
        alternate_label: '',
        installed_seats_name: '',
        follow_people_upper: '0',
        companion: 0,
      });
      dispatch({
        type: PUT_CANCEL_RESERVE_INITIAL,
      });
    }
  }, [modalCancelReserveShow]);

  const handleCancelReserve = (scheduleId: number, contentsId: number) => () => {
    dispatch({
      type: PUT_CANCEL_RESERVE_REQUEST,
      contentsId,
      scheduleId,
      history,
    });
  };

  const onHideCancelReserveModal = () => {
    setModalCancelReserveShow(false);
  };

  useEffect(() => {
    if (isPutCancelReserveLoaded) {
      onHideCancelReserveModal();
      fetchReserveHistoryData();
      setIsCancelReserveSuccess(true);
    }
  }, [isPutCancelReserveLoaded]);

  const onClickRemoveWaitCancel = (item: ReserveDataType) => () => {
    if (item.data_id) {
      setModalRemoveWaitCancelShow(true);
      setModalRemoveWaitCancelData({
        data_id: item.data_id,
        contents_annex_flag: item.contents_annex_flag,
        contents_web_show_name: item.contents_web_show_name,
        club_equipment_annex_flag: item.club_equipment_annex_flag,
        club_web_show_name: item.club_web_show_name,
        reserve_time_label: item.reserve_time_label,
      });
    }
  };

  useEffect(() => {
    if (!modalRemoveWaitCancelShow) {
      setModalRemoveWaitCancelData({
        data_id: 0,
        contents_annex_flag: 0,
        contents_web_show_name: '',
        club_equipment_annex_flag: 0,
        club_web_show_name: '',
        reserve_time_label: '',
      });
    }
  }, [modalRemoveWaitCancelShow]);

  const handleRemoveWaitCancel = (contentsTimeCancelWaitId: number) => () => {
    dispatch({
      type: PUT_REMOVE_WAIT_CANCEL_REQUEST,
      contentsTimeCancelWaitId,
      history,
    });
  };

  const onHideRemoveWaitCancelModal = () => {
    setModalRemoveWaitCancelShow(false);
  };

  useEffect(() => {
    if (isPutRemoveWaitCancelLoaded) {
      onHideRemoveWaitCancelModal();
      fetchReserveHistoryData();
      setIsRemoveWaitCancelSuccess(true);
    }
  }, [isPutRemoveWaitCancelLoaded]);

  return (
    <>
      <MyPageLayout>
        <CustomLoading isLoading={!isGetReserveHistoryLoaded} heightSize={50} isOverall />
        <div className="reservationHistoryPage">
          <main className="main-layout l-main l-main--medium">
            <section className="p-title-area p-title-area--padding-bottom65">
              <p className="c-title">予約履歴</p>
            </section>

            <section className="p-content-area p-content-area--padding-x0-b200 u-gap25">
              {/* <!-- 年月選択 --> */}
              <div className="p-select-arrow">
                <button
                  type="button"
                  className="p-select-arrow__button p-select-arrow__button--prev"
                  aria-label="前の月へ"
                  onClick={onClickPrevYearMonth}
                  disabled={curYearMonthIndex >= yearMonthList.length - 1}
                />
                <div className="p-select-arrow__display">
                  <div className="c-borderless-selector c-borderless-selector--width175">
                    <select
                      name="store"
                      className="c-borderless-selector__window u-pr0 u-fs-24"
                      value={yearMonthList[curYearMonthIndex]}
                      onChange={onChangeYearMonth}
                    >
                      <option hidden>店舗を選択する</option>
                      {yearMonthList.map((rowItem, rowIndex) => {
                        return (
                          <option value={rowItem}>
                            {moment(rowItem, 'YYYYMM').startOf('month').format('YYYY年M月')}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
                <button
                  type="button"
                  className="p-select-arrow__button p-select-arrow__button--next"
                  aria-label="次の月へ"
                  onClick={onClickNextYearMonth}
                  disabled={curYearMonthIndex <= 0}
                />
              </div>

              {isGetReserveHistoryLoaded &&
              getReserveHistory.data &&
              ((getReserveHistory.data.now_reserve_list && getReserveHistory.data.now_reserve_list.length > 0) ||
                (getReserveHistory.data.wait_cancel_list && getReserveHistory.data.wait_cancel_list.length > 0) ||
                (getReserveHistory.data.past_reserve_list && getReserveHistory.data.past_reserve_list.length > 0)) ? (
                <>
                  <div className="p-content-area__column u-gap30">
                    {getReserveHistory.data.now_reserve_list && getReserveHistory.data.now_reserve_list.length > 0 ? (
                      <>
                        {/* <!-- 現在の予約 --> */}
                        <div className="p-content-area__column p-content-area__column--flex-start u-gap10">
                          <div className="c-title c-title--medium">現在の予約</div>

                          <div className="p-content-area__column u-gap20">
                            {/* <!-- 予約内容 --> */}
                            {getReserveHistory.data.now_reserve_list.map((rowItem, rowIndex) => {
                              return (
                                <div className="p-reservation-card p-reservation-card--box-shadow p-reservation-card--flex-start u-gap10">
                                  <div className="p-reservation-card__header p-reservation-card__header--flex-start">
                                    <p className="p-reservation-card__row-text">
                                      <span className="p-reservation-card__strong-text">
                                        {rowItem.reserve_time_label}
                                      </span>
                                    </p>
                                    <div className="p-reservation-card__title p-reservation-card__title--medium">
                                      {rowItem.contents_web_show_name}
                                    </div>
                                  </div>

                                  <div className="p-reservation-card__details p-reservation-card__details--wide">
                                    <dl className="p-detail-list u-gap5 u-sp-gap15">
                                      {/* <!-- アコーディオン --> */}
                                      <div className="p-detail-list__accordion-box p-detail-list__accordion-box--gap10-5">
                                        <div className="p-detail-list__accordion-label p-detail-list__accordion-label--small">
                                          店舗名
                                        </div>
                                        <div className="p-detail-list__accordion-value p-detail-list__accordion-value--small u-fw-500">
                                          {rowItem.club_web_show_name}
                                          {rowItem.club_equipment_annex_flag === 1 ? (
                                            <>
                                              <span className="c-genre-label c-genre-label--large">別館</span>
                                            </>
                                          ) : (
                                            <></>
                                          )}
                                        </div>

                                        {/* <!-- アコーディオンボタン --> */}
                                        <details className="c-accordion p-detail-list__store-accordion">
                                          <summary className="p-detail-list__link-title">店舗詳細を確認</summary>
                                        </details>

                                        {/* <!-- アコーディオンコンテンツ --> */}
                                        <div className="p-detail-list__link-content">
                                          <dl className="p-detail-list__inner u-gap5 u-sp-gap15">
                                            <div className="p-detail-list__item">
                                              <dt className="p-detail-list__label p-detail-list__label--small">
                                                営業時間
                                              </dt>
                                              <dd className="p-detail-list__value p-detail-list__value--small u-fw-500">
                                                {rowItem.open_date
                                                  ?.split(/[\r\n]+/)
                                                  .map((s) => s.trim())
                                                  .filter(Boolean)
                                                  .map((item, index) => {
                                                    return (
                                                      <span>
                                                        {item}
                                                        <br />
                                                      </span>
                                                    );
                                                  })}
                                              </dd>
                                            </div>
                                            <div className="p-detail-list__item">
                                              <dt className="p-detail-list__label p-detail-list__label--small">
                                                電話番号
                                              </dt>
                                              <dd className="p-detail-list__value p-detail-list__value--small u-fw-500">
                                                {rowItem.telephone_number ? (
                                                  <>
                                                    <a
                                                      href={`tel:${rowItem.telephone_number.replaceAll('-', '')}`}
                                                      className="p-detail-list__tel"
                                                    >
                                                      {rowItem.telephone_number}
                                                    </a>
                                                  </>
                                                ) : (
                                                  <></>
                                                )}
                                              </dd>
                                            </div>
                                            <div className="p-detail-list__item">
                                              <dt className="p-detail-list__label p-detail-list__label--small">住所</dt>
                                              <dd className="p-detail-list__value p-detail-list__value--small u-fw-500">
                                                {/* 〒444-0825 静岡県富士宮市弓沢町703 */}
                                                {rowItem.post_id ? `〒${rowItem.post_id}` : ''}
                                                {rowItem.post_id && (rowItem.address || rowItem.addr_buildname)
                                                  ? ' '
                                                  : ''}
                                                {rowItem.address ? rowItem.address : ''}
                                                {rowItem.addr_buildname ? rowItem.addr_buildname : ''}
                                              </dd>
                                            </div>
                                            {rowItem.contents_annex_flag === 1 ? (
                                              <>
                                                <div className="p-detail-list__item">
                                                  <dt className="p-detail-list__label p-detail-list__label--small">
                                                    別館住所
                                                  </dt>
                                                  <dd className="p-detail-list__value p-detail-list__value--small u-fw-500">
                                                    {/* 〒444-0825 静岡県富士宮市弓沢町705 */}
                                                    {rowItem.annex_post_id ? `〒${rowItem.annex_post_id}` : ''}
                                                    {rowItem.annex_post_id &&
                                                    (rowItem.annex_address || rowItem.annex_addr_buildname)
                                                      ? ' '
                                                      : ''}
                                                    {rowItem.annex_address ? rowItem.annex_address : ''}
                                                    {rowItem.annex_addr_buildname ? rowItem.annex_addr_buildname : ''}
                                                  </dd>
                                                </div>
                                              </>
                                            ) : (
                                              <></>
                                            )}
                                            {rowItem.contents_classification === 0 && rowItem.layout_picture ? (
                                              <>
                                                <div className="p-detail-list__item p-detail-list__item--flex-start">
                                                  <dt className="p-detail-list__label p-detail-list__label--small">
                                                    配置図
                                                  </dt>
                                                  <dd className="p-detail-list__value p-detail-list__value--small u-fw-500 p-detail-list__value p-detail-list__value--small--width-full">
                                                    <picture className="p-detail-list__picture">
                                                      <img
                                                        loading="lazy"
                                                        className="p-detail-list__image"
                                                        src={rowItem.layout_picture}
                                                        alt=""
                                                      />
                                                    </picture>
                                                  </dd>
                                                </div>
                                              </>
                                            ) : (
                                              <></>
                                            )}
                                          </dl>
                                        </div>
                                      </div>

                                      <div className="p-detail-list__item">
                                        <dt className="p-detail-list__label p-detail-list__label--small">スペース</dt>
                                        <dd className="p-detail-list__value p-detail-list__value--small u-fw-500">
                                          {/* A・レンタルクラブ設置席 */}
                                          {rowItem.alternate_label ? rowItem.alternate_label : ''}
                                          {rowItem.alternate_label && rowItem.installed_seats_name ? '・' : ''}
                                          {rowItem.installed_seats_name ? rowItem.installed_seats_name : ''}
                                        </dd>
                                      </div>
                                      {rowItem.follow_people_upper != null &&
                                      Number(rowItem.follow_people_upper) > 0 ? (
                                        <>
                                          <div className="p-detail-list__item">
                                            <dt className="p-detail-list__label p-detail-list__label--small">同行者</dt>
                                            <dd className="p-detail-list__value p-detail-list__value--small u-fw-500">
                                              {rowItem.companion ? rowItem.companion : 0}名
                                            </dd>
                                          </div>
                                        </>
                                      ) : (
                                        <></>
                                      )}
                                    </dl>
                                  </div>

                                  <div className="p-reservation-card__footer u-pt15 u-pb20">
                                    <div className="p-reservation-card__cancel-info">
                                      <button
                                        type="button"
                                        className="p-reservation-card__cancel-link c-link"
                                        onClick={onClickCancelReserve(rowItem)}
                                        disabled={
                                          false &&
                                          !(rowItem.cancel_time && moment().isBefore(moment(rowItem.cancel_time)))
                                        }
                                      >
                                        予約をキャンセルする
                                      </button>
                                      <p className="p-reservation-card__cancel-deadline">
                                        {`${rowItem.cancel_time_label} までキャンセル可能`}
                                      </p>
                                    </div>
                                    <div className="p-reservation-card__google-calendar">
                                      <button
                                        type="button"
                                        className="p-reservation-card__cancel-link c-link"
                                        onClick={onClickGoogleCalendarAdd(rowItem)}
                                      >
                                        <img
                                          src={googleCalendarIcon}
                                          alt="Googleカレンダーに追加する"
                                          className="p-reservation-card__google-calendar-icon"
                                        />
                                        <span>Googleカレンダーに追加する</span>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    ) : (
                      <></>
                    )}

                    {getReserveHistory.data.wait_cancel_list && getReserveHistory.data.wait_cancel_list.length > 0 ? (
                      <>
                        {/* <!-- キャンセル待ちの予約 --> */}
                        <div className="p-content-area__column p-content-area__column--flex-start u-gap10 u-pt5">
                          <div className="c-title c-title--medium">キャンセル待ちの予約</div>

                          <div className="p-content-area__column u-gap20">
                            {/* <!-- 予約内容 --> */}
                            {getReserveHistory.data.wait_cancel_list.map((rowItem, rowIndex) => {
                              return (
                                <div className="p-reservation-card p-reservation-card--box-shadow p-reservation-card--flex-start p-reservation-card--gray5-background u-gap10">
                                  <div className="p-reservation-card__header p-reservation-card__header--flex-start">
                                    <p className="p-reservation-card__row-text">
                                      <span className="p-reservation-card__strong-text">
                                        {rowItem.reserve_time_label}
                                      </span>
                                    </p>
                                    <div className="p-reservation-card__title p-reservation-card__title--medium">
                                      {rowItem.contents_web_show_name}
                                    </div>
                                  </div>

                                  <div className="p-reservation-card__details p-reservation-card__details--wide">
                                    <dl className="p-detail-list u-gap5 u-sp-gap15">
                                      {/* <!-- アコーディオン --> */}
                                      <div className="p-detail-list__accordion-box p-detail-list__accordion-box--gap10-5">
                                        <div className="p-detail-list__accordion-label p-detail-list__accordion-label--small">
                                          店舗名
                                        </div>
                                        <div className="p-detail-list__accordion-value p-detail-list__accordion-value--small u-fw-500">
                                          {rowItem.club_web_show_name}
                                          {rowItem.club_equipment_annex_flag === 1 ? (
                                            <>
                                              <span className="c-genre-label c-genre-label--large">別館</span>
                                            </>
                                          ) : (
                                            <></>
                                          )}
                                        </div>

                                        {/* <!-- アコーディオンボタン --> */}
                                        <details className="c-accordion p-detail-list__store-accordion">
                                          <summary className="p-detail-list__link-title">店舗詳細を確認</summary>
                                        </details>

                                        {/* <!-- アコーディオンコンテンツ --> */}
                                        <div className="p-detail-list__link-content">
                                          <dl className="p-detail-list__inner u-gap5 u-sp-gap15">
                                            <div className="p-detail-list__item">
                                              <dt className="p-detail-list__label p-detail-list__label--small">
                                                営業時間
                                              </dt>
                                              <dd className="p-detail-list__value p-detail-list__value--small u-fw-500">
                                                {rowItem.open_date
                                                  ?.split(/[\r\n]+/)
                                                  .map((s) => s.trim())
                                                  .filter(Boolean)
                                                  .map((item, index) => {
                                                    return (
                                                      <span>
                                                        {item}
                                                        <br />
                                                      </span>
                                                    );
                                                  })}
                                              </dd>
                                            </div>
                                            <div className="p-detail-list__item">
                                              <dt className="p-detail-list__label p-detail-list__label--small">
                                                電話番号
                                              </dt>
                                              <dd className="p-detail-list__value p-detail-list__value--small u-fw-500">
                                                {rowItem.telephone_number ? (
                                                  <>
                                                    <a
                                                      href={`tel:${rowItem.telephone_number.replaceAll('-', '')}`}
                                                      className="p-detail-list__tel"
                                                    >
                                                      {rowItem.telephone_number}
                                                    </a>
                                                  </>
                                                ) : (
                                                  <></>
                                                )}
                                              </dd>
                                            </div>
                                            <div className="p-detail-list__item">
                                              <dt className="p-detail-list__label p-detail-list__label--small">住所</dt>
                                              <dd className="p-detail-list__value p-detail-list__value--small u-fw-500">
                                                {rowItem.post_id ? `〒${rowItem.post_id}` : ''}
                                                {rowItem.post_id && (rowItem.address || rowItem.addr_buildname)
                                                  ? ' '
                                                  : ''}
                                                {rowItem.address ? rowItem.address : ''}
                                                {rowItem.addr_buildname ? rowItem.addr_buildname : ''}
                                              </dd>
                                            </div>
                                            {rowItem.contents_annex_flag === 1 ? (
                                              <>
                                                <div className="p-detail-list__item">
                                                  <dt className="p-detail-list__label p-detail-list__label--small">
                                                    別館住所
                                                  </dt>
                                                  <dd className="p-detail-list__value p-detail-list__value--small u-fw-500">
                                                    {rowItem.annex_post_id ? `〒${rowItem.annex_post_id}` : ''}
                                                    {rowItem.annex_post_id &&
                                                    (rowItem.annex_address || rowItem.annex_addr_buildname)
                                                      ? ' '
                                                      : ''}
                                                    {rowItem.annex_address ? rowItem.annex_address : ''}
                                                    {rowItem.annex_addr_buildname ? rowItem.annex_addr_buildname : ''}
                                                  </dd>
                                                </div>
                                              </>
                                            ) : (
                                              <></>
                                            )}
                                            {rowItem.contents_classification === 0 && rowItem.layout_picture ? (
                                              <>
                                                <div className="p-detail-list__item p-detail-list__item--flex-start">
                                                  <dt className="p-detail-list__label p-detail-list__label--small">
                                                    配置図
                                                  </dt>
                                                  <dd className="p-detail-list__value p-detail-list__value--small u-fw-500 p-detail-list__value p-detail-list__value--small--width-full">
                                                    <picture className="p-detail-list__picture">
                                                      <img
                                                        loading="lazy"
                                                        className="p-detail-list__image"
                                                        src={rowItem.layout_picture}
                                                        alt=""
                                                      />
                                                    </picture>
                                                  </dd>
                                                </div>
                                              </>
                                            ) : (
                                              <></>
                                            )}
                                          </dl>
                                        </div>
                                      </div>
                                    </dl>
                                  </div>

                                  <div className="p-reservation-card__footer u-pt15 u-pb20">
                                    <div className="p-reservation-card__cancel-info">
                                      <button
                                        type="button"
                                        className="p-reservation-card__cancel-link c-link"
                                        onClick={onClickRemoveWaitCancel(rowItem)}
                                      >
                                        キャンセル待ちを解除する
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    ) : (
                      <></>
                    )}

                    {getReserveHistory.data.past_reserve_list && getReserveHistory.data.past_reserve_list.length > 0 ? (
                      <>
                        {/* <!-- 履歴 --> */}
                        <div className="p-content-area__column p-content-area__column--flex-start u-gap10 u-pt5">
                          <div className="c-title c-title--medium">履歴</div>

                          <div className="p-scroll-container p-scroll-container--border c-scroll c-scroll--x">
                            <table className="p-table">
                              <thead>
                                <tr>
                                  <th className="c-cell c-cell--thead c-cell--width245">日時</th>
                                  <th className="c-cell c-cell--thead">プログラム</th>
                                  <th className="c-cell c-cell--thead c-cell--width226">店舗名</th>
                                  <th className="c-cell c-cell--thead c-cell--center c-cell--width80">スペース</th>
                                  <th className="c-cell c-cell--thead c-cell--center c-cell--width80">同行者</th>
                                </tr>
                              </thead>

                              <tbody>
                                {getReserveHistory.data.past_reserve_list.map((rowItem, rowIndex) => {
                                  return (
                                    <tr>
                                      <td className="c-cell">{rowItem.reserve_time_label}</td>
                                      <td className="c-cell">
                                        <span className="p-table__text-box">{rowItem.contents_web_show_name}</span>
                                      </td>
                                      <td className="c-cell">
                                        <span className="p-table__text-box">{rowItem.club_web_show_name}</span>
                                      </td>
                                      <td className="c-cell c-cell--center">
                                        {rowItem.alternate_label ? rowItem.alternate_label : ''}
                                      </td>
                                      <td className="c-cell c-cell--right">
                                        {rowItem.follow_people_upper != null &&
                                        Number(rowItem.follow_people_upper) > 0 ? (
                                          <>{rowItem.companion ? rowItem.companion : 0}人</>
                                        ) : (
                                          <>0人</>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </>
                    ) : (
                      <></>
                    )}
                  </div>
                </>
              ) : isGetReserveHistoryLoaded ? (
                <>
                  {/* <!-- 全データがない場合 --> */}
                  <p className="c-text">該当する予約はありません。</p>
                </>
              ) : (
                <></>
              )}
            </section>
          </main>
        </div>

        <ReservationCancelModal
          show={modalCancelReserveShow}
          data={modalCancelReserveData}
          isDisabled={isPutCancelReserveLoading}
          handle={handleCancelReserve}
          err={cancelReserveErrMessage}
          onHide={onHideCancelReserveModal}
        />
        <ReservationCancelCompleteModal show={isCancelReserveSuccess} onHide={() => setIsCancelReserveSuccess(false)} />

        <CancelWaitRemoveModal
          show={modalRemoveWaitCancelShow}
          data={modalRemoveWaitCancelData}
          handle={handleRemoveWaitCancel}
          onHide={onHideRemoveWaitCancelModal}
        />
        <CancelWaitRemoveCompleteModal
          show={isRemoveWaitCancelSuccess}
          onHide={() => setIsRemoveWaitCancelSuccess(false)}
        />
      </MyPageLayout>
    </>
  );
};

export default ReservationHistory;
